import React, { useEffect, useMemo, useState } from 'react';
import { fetchPlatformSupportAlerts, type PlatformSupportAlert, type PlatformSupportAlertList } from '../services/platformSupportAlerts';
import './PlatformSupportAlertsWorkspace.css';

const TYPE_LABELS: Record<string, string> = {
  DORMANT_SEAT: 'Dormant seat', NEVER_USED_SEAT: 'Never-used seat', INACTIVE_ACCOUNT: 'Inactive account',
  SEAT_EXPIRY: 'Seat expiry', SEAT_CAPACITY: 'Seat capacity', REPEATED_LOGIN_FAILURE: 'Login failures', LOCATION_CHANGE: 'Location change'
};
const EVIDENCE_LABELS: Record<string, string> = {
  assigned: 'Seats assigned', capacity: 'Seat capacity', available: 'Seats available', utilizationPercent: 'Utilisation',
  remainingDays: 'Days remaining', nonRenewingQuantity: 'Seats not renewing', productCode: 'Seat code', productLabel: 'Seat product',
  seatProduct: 'Seat code', licenseNumber: 'Licence', observedDays: 'Days observed', dormantDays: 'Days since login',
  lastSuccessfulLoginAt: 'Last successful login', lastActiveAt: 'Last active', monitoringStartedAt: 'Monitoring started',
  inactiveDays: 'Inactive days', lastActivityAt: 'Last activity', failureCount: 'Failed attempts', windowMinutes: 'Time window',
  ipAddress: 'IP address', countryCode: 'Country', countries: 'Countries', windowHours: 'Time window'
};

function typeLabel(type: string) {
  return TYPE_LABELS[type] || type.toLowerCase().replace(/_/g, ' ').replace(/^./, value => value.toUpperCase());
}
function formatDateTime(value: string) {
  return new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}
function alertAccount(alert: PlatformSupportAlert) {
  if (alert.target.type === 'USER') return alert.target.organization || null;
  if (['UNIT', 'GROUP', 'ORG'].includes(alert.target.type)) return { id: alert.target.id, name: alert.target.name, slug: null };
  return alert.target.organization || null;
}
function seatLabel(alert: PlatformSupportAlert) {
  return String(alert.evidence.productLabel || alert.evidence.productCode || alert.evidence.seatProduct || '');
}
function evidenceValue(key: string, value: unknown) {
  if (value === null || value === undefined || value === '') return 'Not recorded';
  if (key === 'utilizationPercent') return `${value}%`;
  if (key === 'windowMinutes') return `${value} minutes`;
  if (key === 'windowHours') return `${value} hours`;
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' && /At$/.test(key) && !Number.isNaN(new Date(value).getTime())) return formatDateTime(value);
  return String(value);
}

const SEVERITY_RANK: Record<PlatformSupportAlert['severity'], number> = { CRITICAL: 0, WARNING: 1, INFO: 2 };

type AlertGroup = {
  key: string;
  type: string;
  severity: PlatformSupportAlert['severity'];
  account: ReturnType<typeof alertAccount>;
  records: PlatformSupportAlert[];
  observedAt: string;
};

function groupAlerts(records: PlatformSupportAlert[]): AlertGroup[] {
  const groups = new Map<string, AlertGroup>();
  for (const alert of records) {
    const account = alertAccount(alert);
    const ownerKey = account?.id || `${alert.target.type}:${alert.target.id}`;
    const key = `${ownerKey}:${alert.type}`;
    const group = groups.get(key);
    if (!group) {
      groups.set(key, { key, type: alert.type, severity: alert.severity, account, records: [alert], observedAt: alert.observedAt });
      continue;
    }
    group.records.push(alert);
    if (SEVERITY_RANK[alert.severity] < SEVERITY_RANK[group.severity]) group.severity = alert.severity;
    if (new Date(alert.observedAt) > new Date(group.observedAt)) group.observedAt = alert.observedAt;
  }
  return Array.from(groups.values());
}

function groupTitle(group: AlertGroup) {
  if (group.records.length === 1) return group.records[0].title;
  if (group.type === 'SEAT_CAPACITY') return `${group.records.length} seat pools need attention`;
  if (group.type === 'SEAT_EXPIRY') return `${group.records.length} seat plans are approaching expiry`;
  if (group.type === 'DORMANT_SEAT' || group.type === 'NEVER_USED_SEAT') return `${group.records.length} licensed users need review`;
  if (group.type === 'REPEATED_LOGIN_FAILURE') return `${group.records.length} users have repeated login failures`;
  return `${group.records.length} related ${typeLabel(group.type).toLowerCase()} alerts`;
}

function alertSubject(alert: PlatformSupportAlert) {
  if (alert.target.type === 'USER') return alert.target.name;
  return seatLabel(alert) || alert.title;
}

function alertSummary(alert: PlatformSupportAlert) {
  if (alert.type === 'SEAT_CAPACITY') return `${alert.evidence.assigned} of ${alert.evidence.capacity} assigned · ${alert.evidence.available} available · ${alert.evidence.utilizationPercent}% used`;
  if (alert.type === 'SEAT_EXPIRY') return `${alert.evidence.nonRenewingQuantity} not renewing · ${alert.evidence.remainingDays} days remaining`;
  if (alert.type === 'DORMANT_SEAT') return `${alert.evidence.dormantDays} days since login · ${seatLabel(alert)}`;
  if (alert.type === 'NEVER_USED_SEAT') return `No successful login · ${seatLabel(alert)}`;
  if (alert.type === 'REPEATED_LOGIN_FAILURE') return `${alert.evidence.failureCount} failed attempts in ${alert.evidence.windowMinutes} minutes`;
  if (alert.type === 'INACTIVE_ACCOUNT') return `${alert.evidence.inactiveDays} days without activity`;
  if (alert.type === 'LOCATION_CHANGE') return `${Array.isArray(alert.evidence.countries) ? alert.evidence.countries.join(', ') : ''} within ${alert.evidence.windowHours} hours`;
  return alert.message;
}

type Props = { token: string; onReviewLicensing?: (accountName: string) => void; onReviewActivity?: (userId?: string) => void };

const PlatformSupportAlertsWorkspace: React.FC<Props> = ({ token, onReviewLicensing, onReviewActivity }) => {
  const [data, setData] = useState<PlatformSupportAlertList | null>(null);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [accountId, setAccountId] = useState('');
  const [seatProduct, setSeatProduct] = useState('');
  const [userId, setUserId] = useState('');
  const [severity, setSeverity] = useState('ALL');
  const [type, setType] = useState('ALL');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setBusy(true);
    void fetchPlatformSupportAlerts(token, { page, pageSize: 50, severity, type, search, accountId, seatProduct, userId })
      .then(setData)
      .catch(caught => setError(caught instanceof Error ? caught.message : 'Proactive support alerts could not be loaded'))
      .finally(() => setBusy(false));
  }, [accountId, page, refreshKey, search, seatProduct, severity, token, type, userId]);
  useEffect(() => {
    const timer = window.setInterval(() => setRefreshKey(value => value + 1), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const availableUsers = useMemo(() => (data?.filterOptions.users || []).filter(user => !accountId || user.accountIds.includes(accountId)), [accountId, data]);
  const availableSeats = useMemo(() => (data?.filterOptions.seatProducts || []).filter(product => !accountId || product.accountIds.includes(accountId)), [accountId, data]);
  const alertGroups = useMemo(() => groupAlerts(data?.records || []), [data]);
  const hasStructuredFilters = Boolean(accountId || seatProduct || userId || severity !== 'ALL' || type !== 'ALL');

  function selectAccount(nextAccountId: string) {
    setAccountId(nextAccountId);
    if (userId && nextAccountId && !data?.filterOptions.users.find(user => user.id === userId)?.accountIds.includes(nextAccountId)) setUserId('');
    if (seatProduct && nextAccountId && !data?.filterOptions.seatProducts.find(product => product.code === seatProduct)?.accountIds.includes(nextAccountId)) setSeatProduct('');
    setPage(1);
  }
  function selectUser(nextUserId: string) {
    setUserId(nextUserId);
    if (nextUserId) {
      const accountIds = data?.filterOptions.users.find(user => user.id === nextUserId)?.accountIds || [];
      if (accountIds.length === 1) setAccountId(accountIds[0]);
    }
    setPage(1);
  }
  function clearFilters() {
    setSearchDraft(''); setSearch(''); setAccountId(''); setSeatProduct(''); setUserId(''); setSeverity('ALL'); setType('ALL'); setPage(1);
  }

  return <div className="platform-alerts-workspace">
    <div className="platform-alerts-heading">
      <div><div className="platform-alerts-eyebrow">BONZAIQ OPERATIONS</div><h1>Proactive alerts</h1><p>Find access, adoption, licence, and capacity risks before they become customer issues.</p></div>
      <button type="button" onClick={() => setRefreshKey(value => value + 1)} disabled={busy}>{busy ? 'Refreshing…' : 'Refresh'}</button>
    </div>
    <div className="platform-alerts-summary">
      <div><strong>{data?.summary.total ?? 0}</strong><span>Open signals</span></div>
      <div className="is-critical"><strong>{data?.summary.critical ?? 0}</strong><span>Critical</span></div>
      <div className="is-warning"><strong>{data?.summary.warning ?? 0}</strong><span>Warning</span></div>
      <div className="is-info"><strong>{data?.summary.info ?? 0}</strong><span>Information</span></div>
    </div>
    <section className="platform-alerts-filters" aria-label="Proactive alert filters">
      <form onSubmit={event => { event.preventDefault(); setSearch(searchDraft.trim()); setPage(1); }}>
        <label htmlFor="platform-alert-search">Search alerts</label>
        <div><input id="platform-alert-search" value={searchDraft} onChange={event => setSearchDraft(event.target.value)} placeholder="Name, licence, IP, or alert detail" /><button type="submit">Search</button></div>
      </form>
      <label>UNIT / ORG<select value={accountId} onChange={event => selectAccount(event.target.value)}><option value="">All accounts</option>{(data?.filterOptions.accounts || []).map(account => <option key={account.id} value={account.id}>{account.name} ({account.type})</option>)}</select></label>
      <label>Seat product<select value={seatProduct} onChange={event => { setSeatProduct(event.target.value); setPage(1); }}><option value="">All seat products</option>{availableSeats.map(product => <option key={product.code} value={product.code}>{product.label}</option>)}</select></label>
      <label>User<select value={userId} onChange={event => selectUser(event.target.value)}><option value="">All users</option>{availableUsers.map(user => <option key={user.id} value={user.id}>{user.name}</option>)}</select></label>
      <label>Severity<select value={severity} onChange={event => { setSeverity(event.target.value); setPage(1); }}><option value="ALL">All severities</option><option value="CRITICAL">Critical</option><option value="WARNING">Warning</option><option value="INFO">Information</option></select></label>
      <label>Alert type<select value={type} onChange={event => { setType(event.target.value); setPage(1); }}><option value="ALL">All alert types</option>{(data?.types || []).map(value => <option key={value} value={value}>{typeLabel(value)}</option>)}</select></label>
      {(search || searchDraft || hasStructuredFilters) ? <button className="platform-alerts-clear" type="button" onClick={clearFilters}>Clear all filters</button> : null}
    </section>
    <section className="platform-alerts-list" aria-label="Current proactive alerts">
      <div className="platform-alerts-list-heading"><div><strong>{alertGroups.length} {alertGroups.length === 1 ? 'issue' : 'issues'} shown</strong><span>{data?.pagination.total ?? 0} matching alert {(data?.pagination.total ?? 0) === 1 ? 'signal' : 'signals'}, grouped by UNIT/ORG and problem. Advisory only—customer access is never changed automatically.</span></div>{data?.pagination.total ? <span>{(data.pagination.page - 1) * data.pagination.pageSize + 1}–{Math.min(data.pagination.total, data.pagination.page * data.pagination.pageSize)} of {data.pagination.total} signals</span> : null}</div>
      <div className="platform-alerts-records">
        {alertGroups.map(group => {
          const first = group.records[0];
          const expanded = expandedKey === group.key;
          const scopeLabel = first.target.type === 'GROUP' ? 'ORG' : group.account ? 'UNIT' : first.target.type;
          const userId = group.records.length === 1 && first.target.type === 'USER' ? first.target.id : undefined;
          return <article key={group.key} className={`platform-alert-card is-${group.severity.toLowerCase()} ${group.records.length > 1 ? 'is-grouped' : ''}`}>
            <div className="platform-alert-card-top"><div><span className="platform-alert-severity">{group.severity}</span><span className="platform-alert-type">{typeLabel(group.type)}</span></div><time>{formatDateTime(group.observedAt)}</time></div>
            <div className="platform-alert-identity"><div><span>{scopeLabel}</span><h2>{group.account?.name || group.account?.slug || first.target.name || 'Unassigned account'}</h2></div>{group.records.length > 1 ? <strong className="platform-alert-count">{group.records.length} affected</strong> : null}</div>
            <h3>{groupTitle(group)}</h3>
            <div className="platform-alert-items">{group.records.map(alert => <div key={alert.key} className="platform-alert-item"><strong>{alertSubject(alert)}</strong><span>{alertSummary(alert)}</span></div>)}</div>
            <div className="platform-alert-actions">
              <button type="button" onClick={() => setExpandedKey(expanded ? null : group.key)} aria-expanded={expanded}>{expanded ? 'Hide details' : 'Show details'}</button>
              {group.account && onReviewLicensing ? <button type="button" onClick={() => onReviewLicensing(group.account?.name || group.account?.slug || '')}>Review licensing</button> : null}
              {onReviewActivity ? <button type="button" onClick={() => onReviewActivity(userId)}>Review activity</button> : null}
            </div>
            {expanded ? <div className="platform-alert-evidence">
              <div className="platform-alert-recommendation"><strong>Recommended next step</strong><span>{Array.from(new Set(group.records.map(alert => alert.recommendation))).join(' ')}</span></div>
              {group.records.map(alert => <section key={alert.key} className="platform-alert-evidence-item"><h4>{alertSubject(alert)}</h4><p>{alert.message}</p><dl>{Object.entries(alert.evidence).filter(([key]) => key !== 'userCreatedDuringCoverage').map(([key, value]) => <div key={key}><dt>{EVIDENCE_LABELS[key] || typeLabel(key)}</dt><dd>{evidenceValue(key, value)}</dd></div>)}</dl></section>)}
            </div> : null}
          </article>;
        })}
        {!busy && !(data?.records.length) ? <div className="platform-alerts-empty">No proactive alerts match these filters.</div> : null}
      </div>
      {data && data.pagination.totalPages > 1 ? <div className="platform-alerts-pagination"><button type="button" disabled={busy || data.pagination.page <= 1} onClick={() => setPage(value => Math.max(1, value - 1))}>Previous</button><span>Page {data.pagination.page} of {data.pagination.totalPages}</span><button type="button" disabled={busy || data.pagination.page >= data.pagination.totalPages} onClick={() => setPage(value => value + 1)}>Next</button></div> : null}
    </section>
    {error ? <div className="platform-alerts-error-backdrop" role="presentation"><section className="platform-alerts-error-dialog" role="alertdialog" aria-modal="true" aria-labelledby="platform-alerts-error-title"><h2 id="platform-alerts-error-title">Proactive alerts could not be loaded</h2><p>{error}</p><button type="button" onClick={() => setError(null)} autoFocus>Close</button></section></div> : null}
  </div>;
};

export default PlatformSupportAlertsWorkspace;
